import { Facebook, Linkedin, Twitter } from "lucide-react";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useFooterSettings } from "@/hooks/useFooterSettings";
import { Link } from "@/lib/router-compat";
import { Skeleton } from "./ui/skeleton";
import { withCurrentYear } from "@/lib/currentYear";

interface FooterProps {
  onNavigate: (page: string) => void;
}

export function Footer({ onNavigate }: FooterProps) {
  const { data: siteSettings, isLoading: siteLoading } = useSiteSettings();
  const { data: footerSettings, isLoading: footerLoading } = useFooterSettings();

  const isInternalLink = (url: string) => url.startsWith("/");

  if (siteLoading || footerLoading || !siteSettings || !footerSettings) {
    return (
      <footer className="bg-muted/40 border-t border-border mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="mb-8">
            {/* Brand skeleton */}
            <div className="mb-10">
              <Skeleton className="h-8 w-28 mb-4" />
              <Skeleton className="h-4 w-full max-w-md mb-2" />
              <Skeleton className="h-4 w-3/4 max-w-md" />
            </div>
            {/* Column skeletons */}
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-8">
              {[1, 2, 3, 4].map((i) => (
                <div key={i}>
                  <Skeleton className="h-5 w-24 mb-4" />
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-5/6" />
                    <Skeleton className="h-4 w-4/6" />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="pt-6 border-t border-border">
            <Skeleton className="h-4 w-64" />
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer className="bg-muted/40 border-t border-border mt-auto">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-10">
        {/* Brand */}
        <div className="mb-12">
          <div className="mb-4">
            {siteSettings.site_logo_url ? (
              <img
                src={siteSettings.site_logo_url}
                alt={siteSettings.site_name}
                className="h-8 w-auto object-contain"
                height="32"
                loading="lazy"
              />
            ) : siteSettings.site_logo_footer_url ? (
              <img
                src={siteSettings.site_logo_footer_url}
                alt={siteSettings.site_name}
                className="h-8 w-auto object-contain"
                height="32"
                loading="lazy"
              />
            ) : null}
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed max-w-md">
            {footerSettings.brand_description}
          </p>
        </div>

        {/* Dynamic Columns */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-8 gap-y-10">
          {footerSettings.columns
            .filter((column) => column.enabled)
            .map((column) => {
              // Fix contact column title if it accidentally contains the email
              const isTitleEmail =
                column.title?.trim().toLowerCase() === (siteSettings.organization_email || "").trim().toLowerCase();
              const displayTitle =
                column.id === "contact" && (isTitleEmail || !column.title?.trim()) ? "Kontakt" : column.title;

              return (
                <div key={column.id}>
                  <h3 className="text-sm font-bold uppercase tracking-wider text-foreground mb-5 pb-2 border-b-2 border-primary/20 inline-block">
                    {displayTitle}
                  </h3>

                  {/* Contact column with email */}
                  {column.id === "contact" && column.show_email && siteSettings.organization_email && (
                    <ul className="space-y-3 mb-4">
                      <li>
                        <a
                          href={`mailto:${siteSettings.organization_email}`}
                          className="text-sm text-muted-foreground hover:text-primary transition-colors"
                        >
                          {siteSettings.organization_email}
                        </a>
                      </li>
                    </ul>
                  )}

                  {/* Regular links column */}
                  {column.links && (
                    <ul className="space-y-3">
                      {column.links
                        .filter((link) => link.enabled)
                        .map((link, index) => (
                          <li key={index}>
                            {isInternalLink(link.url) ? (
                              <Link
                                to={link.url}
                                className="text-sm text-muted-foreground hover:text-primary transition-colors"
                              >
                                {link.label}
                              </Link>
                            ) : (
                              <a
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-muted-foreground hover:text-primary transition-colors"
                              >
                                {link.label}
                              </a>
                            )}
                          </li>
                        ))}
                    </ul>
                  )}

                  {/* Social media icons for contact column */}
                  {column.id === "contact" && footerSettings.show_social_media && (
                    <div className="flex gap-3 mt-5">
                      {siteSettings.organization_social?.facebook && (
                        <a
                          href={siteSettings.organization_social.facebook}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-9 h-9 bg-muted rounded-full flex items-center justify-center text-muted-foreground hover:bg-primary hover:text-primary-foreground transition-colors"
                          aria-label="Facebook"
                        >
                          <Facebook className="w-4 h-4" />
                        </a>
                      )}
                      {siteSettings.organization_social?.twitter && (
                        <a
                          href={siteSettings.organization_social.twitter}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-9 h-9 bg-muted rounded-full flex items-center justify-center text-muted-foreground hover:bg-primary hover:text-primary-foreground transition-colors"
                          aria-label="Twitter"
                        >
                          <Twitter className="w-4 h-4" />
                        </a>
                      )}
                      {siteSettings.organization_social?.linkedin && (
                        <a
                          href={siteSettings.organization_social.linkedin}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-9 h-9 bg-muted rounded-full flex items-center justify-center text-muted-foreground hover:bg-primary hover:text-primary-foreground transition-colors"
                          aria-label="LinkedIn"
                        >
                          <Linkedin className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-border bg-muted/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row justify-between items-center gap-3">
          {footerSettings.bottom_bar.show_copyright && (
            <p className="text-xs text-muted-foreground text-center sm:text-left">
              © {new Date().getFullYear()} {siteSettings.organization_name || siteSettings.site_name}.{" "}
              {withCurrentYear(footerSettings.bottom_bar.copyright_text)}
            </p>
          )}
          {footerSettings.bottom_bar.tagline && (
            <p className="text-sm font-semibold text-primary">{footerSettings.bottom_bar.tagline}</p>
          )}
        </div>
      </div>
    </footer>
  );
}
